import { useState } from "react";

type Material = "aluminium" | "upvc";

const materials = {
  aluminium: {
    name: "Aluminium",
    tagline: "The Premium Choice",
    badge: "Most Popular",
    badgeColor: "bg-amber-600",
    accent: "amber",
    heroImg: "https://public.readdy.ai/ai/img_res/e93bb2363436f9f5f41a530152cf0efd.jpg",
    priceFrom: "£2,800",
    priceTo: "£8,500+",
    description: "Aluminium is the gold standard for bifold doors. Its exceptional strength-to-weight ratio allows for dramatically slimmer frames — meaning more glass, more light, and a cleaner aesthetic. Thermally broken aluminium profiles deliver outstanding energy performance without compromising on looks.",
    specs: [
      { label: "Frame Sightlines", value: "From 45mm", highlight: true },
      { label: "Frame Depth", value: "70mm" },
      { label: "U-Value (frame)", value: "From 1.4 W/m²K", highlight: true },
      { label: "Max Panel Width", value: "1,200mm" },
      { label: "Max Height", value: "3,000mm" },
      { label: "Max Total Width", value: "7,200mm" },
      { label: "Colour Options", value: "200+ RAL colours", highlight: true },
      { label: "Guarantee", value: "10 years" },
      { label: "Maintenance", value: "Very low" },
      { label: "Lifespan", value: "40–50+ years", highlight: true },
    ],
    ratings: [
      { label: "Aesthetics", score: 5 },
      { label: "Thermal Performance", score: 5 },
      { label: "Slim Sightlines", score: 5 },
      { label: "Colour Range", score: 5 },
      { label: "Durability", score: 5 },
      { label: "Value for Money", score: 3 },
    ],
    pros: [
      "Dramatically slimmer frames — up to 45mm sightlines",
      "Powder-coated in any RAL colour, inside and out",
      "Thermally broken for A-rated energy performance",
      "Stronger than uPVC — spans wider openings",
      "Will not warp, bow, or discolour over time",
      "Recyclable — environmentally responsible choice",
      "Suits contemporary and traditional homes equally",
    ],
    cons: [
      "Higher initial investment than uPVC",
      "Slightly longer lead time for bespoke colours",
    ],
    bestFor: ["Contemporary extensions", "Large openings 3m+", "Colour-matched projects", "Long-term investment"],
  },
  upvc: {
    name: "uPVC",
    tagline: "The Budget-Friendly Option",
    badge: "Best Value",
    badgeColor: "bg-emerald-600",
    accent: "emerald",
    heroImg: "https://readdy.ai/api/search-image?query=white%20upvc%20bifold%20door%20residential%20uk%20home%20garden%20patio%20clean%20simple%20modern%20affordable%20double%20glazed&width=600&height=420&seq=mat-upvc-hero&orientation=landscape",
    priceFrom: "£1,800",
    priceTo: "£5,500",
    description: "uPVC bifold doors offer a cost-effective way to open up your home. Modern uPVC profiles have improved significantly and now deliver good thermal performance and a clean, neat appearance. They are a practical choice for homeowners who want the bifold experience at a more accessible price point.",
    specs: [
      { label: "Frame Sightlines", value: "From 90mm" },
      { label: "Frame Depth", value: "70–80mm" },
      { label: "U-Value (frame)", value: "From 1.6 W/m²K" },
      { label: "Max Panel Width", value: "900mm" },
      { label: "Max Height", value: "2,400mm" },
      { label: "Max Total Width", value: "4,800mm" },
      { label: "Colour Options", value: "Limited foil finishes" },
      { label: "Guarantee", value: "10 years" },
      { label: "Maintenance", value: "Low" },
      { label: "Lifespan", value: "20–30 years" },
    ],
    ratings: [
      { label: "Aesthetics", score: 3 },
      { label: "Thermal Performance", score: 4 },
      { label: "Slim Sightlines", score: 2 },
      { label: "Colour Range", score: 2 },
      { label: "Durability", score: 3 },
      { label: "Value for Money", score: 5 },
    ],
    pros: [
      "Lower upfront cost than aluminium",
      "Good thermal performance with modern profiles",
      "Low maintenance — wipe clean",
      "Widely available in white and common foil finishes",
      "Suitable for standard residential openings",
    ],
    cons: [
      "Wider frames reduce glass area and natural light",
      "Limited to narrower spans (max ~4.8m)",
      "Fewer colour options — mostly foil wraps",
      "Can discolour or warp over time in direct sun",
      "Less structural rigidity than aluminium",
    ],
    bestFor: ["Tighter budgets", "Smaller openings under 3m", "White or woodgrain finish", "Rental properties"],
  },
};

const comparisonRows = [
  { label: "Starting Price", al: "From £2,800", upvc: "From £1,800", winner: "upvc" },
  { label: "Frame Sightlines", al: "From 45mm", upvc: "From 90mm", winner: "al" },
  { label: "Max Opening Width", al: "7,200mm", upvc: "4,800mm", winner: "al" },
  { label: "U-Value", al: "From 1.4 W/m²K", upvc: "From 1.6 W/m²K", winner: "al" },
  { label: "Colour Options", al: "200+ RAL colours", upvc: "Limited foil finishes", winner: "al" },
  { label: "Lifespan", al: "40–50+ years", upvc: "20–30 years", winner: "al" },
  { label: "Maintenance", al: "Very low", upvc: "Low", winner: "draw" },
  { label: "Structural Strength", al: "Excellent", upvc: "Good", winner: "al" },
  { label: "Recyclability", al: "Fully recyclable", upvc: "Partially recyclable", winner: "al" },
  { label: "Value for Money", al: "Premium investment", upvc: "Budget-friendly", winner: "upvc" },
];

export default function MaterialsComparison() {
  const [active, setActive] = useState<Material>("aluminium");
  const [showTable, setShowTable] = useState(false);
  const mat = materials[active];

  return (
    <section className="py-20 bg-white" id="materials">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Materials Guide</span>
          <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Aluminium vs uPVC Bifold Doors
          </h2>
          <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Choosing the right material is the most important decision when buying bifold doors. Here&apos;s everything you need to know to make the right choice for your Surrey home.
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-stone-100 rounded-full p-1">
            {(["aluminium", "upvc"] as Material[]).map((m) => (
              <button
                key={m}
                onClick={() => setActive(m)}
                className={`px-8 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  active === m ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-800"
                }`}
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {m === "aluminium" ? "Aluminium" : "uPVC"}
              </button>
            ))}
          </div>
        </div>

        {/* Detail Card */}
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-14">
          {/* Image + badge */}
          <div className="relative w-full h-[380px] rounded-xl overflow-hidden">
            <img src={mat.heroImg} alt={`${mat.name} bifold doors`} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className={`absolute top-4 left-4 ${mat.badgeColor} text-white text-xs font-bold px-3 py-1.5 rounded-full`} style={{ fontFamily: "'Raleway', sans-serif" }}>
              {mat.badge}
            </div>
            <div className="absolute bottom-5 left-5 right-5">
              <p className="text-white text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{mat.name} Bifold Doors</p>
              <p className="text-stone-300 text-sm mt-1" style={{ fontFamily: "'Raleway', sans-serif" }}>{mat.tagline} · {mat.priceFrom} – {mat.priceTo} installed</p>
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="text-zinc-500 text-sm leading-relaxed mb-7" style={{ fontFamily: "'Raleway', sans-serif" }}>{mat.description}</p>

            {/* Ratings */}
            <div className="space-y-3 mb-7">
              {mat.ratings.map((r) => (
                <div key={r.label} className="flex items-center gap-4">
                  <span className="text-zinc-600 text-xs w-36 flex-shrink-0" style={{ fontFamily: "'Raleway', sans-serif" }}>{r.label}</span>
                  <div className="flex-1 bg-stone-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${active === "aluminium" ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${(r.score / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-zinc-400 text-xs w-6 text-right" style={{ fontFamily: "'Raleway', sans-serif" }}>{r.score}/5</span>
                </div>
              ))}
            </div>

            {/* Pros / Cons */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Advantages</p>
                <ul className="space-y-1.5">
                  {mat.pros.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-emerald-500 text-xs"></i></div>
                      <span className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Considerations</p>
                <ul className="space-y-1.5">
                  {mat.cons.map((c) => (
                    <li key={c} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-information-line text-amber-500 text-xs"></i></div>
                      <span className="text-zinc-500 text-xs leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Best for tags */}
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="text-xs text-zinc-500 font-semibold mr-1" style={{ fontFamily: "'Raleway', sans-serif" }}>Best for:</span>
              {mat.bestFor.map((tag) => (
                <span key={tag} className="text-xs bg-stone-100 text-zinc-600 px-3 py-1 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Specs Table */}
        <div className="bg-stone-50 rounded-xl border border-stone-100 overflow-hidden mb-10">
          <div className="px-6 py-4 bg-zinc-900 flex items-center justify-between">
            <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Technical Specifications — {mat.name}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-stone-200">
            {mat.specs.map((s) => (
              <div key={s.label} className={`p-4 border-b border-stone-200 ${s.highlight ? "bg-amber-50/50" : ""}`}>
                <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.label}</p>
                <p className={`font-semibold text-sm ${s.highlight ? "text-amber-700" : "text-zinc-800"}`} style={{ fontFamily: "'Raleway', sans-serif" }}>{s.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Side-by-side comparison table toggle */}
        <div className="text-center mb-6">
          <button
            onClick={() => setShowTable(!showTable)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${showTable ? "ri-subtract-line" : "ri-add-line"}`}></i>
            </div>
            {showTable ? "Hide" : "Show"} Full Side-by-Side Comparison
          </button>
        </div>

        {showTable && (
          <div className="rounded-xl border border-stone-200 overflow-hidden">
            <div className="grid grid-cols-4 bg-zinc-900 text-white text-xs font-bold uppercase tracking-wide">
              <div className="px-5 py-4 col-span-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Feature</div>
              <div className="px-5 py-4 text-amber-400 border-l border-white/10" style={{ fontFamily: "'Raleway', sans-serif" }}>Aluminium</div>
              <div className="px-5 py-4 text-emerald-400 border-l border-white/10" style={{ fontFamily: "'Raleway', sans-serif" }}>uPVC</div>
            </div>
            {comparisonRows.map((row, i) => (
              <div key={row.label} className={`grid grid-cols-4 border-b border-stone-100 ${i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}`}>
                <div className="px-5 py-3.5 col-span-2 text-zinc-700 text-sm font-medium" style={{ fontFamily: "'Raleway', sans-serif" }}>{row.label}</div>
                <div className={`px-5 py-3.5 text-sm border-l border-stone-100 flex items-center gap-2 ${row.winner === "al" ? "text-amber-700 font-semibold" : "text-zinc-500"}`} style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {row.winner === "al" && <div className="w-4 h-4 flex items-center justify-center"><i className="ri-trophy-line text-amber-500 text-xs"></i></div>}
                  {row.al}
                </div>
                <div className={`px-5 py-3.5 text-sm border-l border-stone-100 flex items-center gap-2 ${row.winner === "upvc" ? "text-emerald-700 font-semibold" : "text-zinc-500"}`} style={{ fontFamily: "'Raleway', sans-serif" }}>
                  {row.winner === "upvc" && <div className="w-4 h-4 flex items-center justify-center"><i className="ri-trophy-line text-emerald-500 text-xs"></i></div>}
                  {row.upvc}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Decision helper */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-600 rounded-lg"><i className="ri-star-line text-white text-base"></i></div>
              <h4 className="font-bold text-zinc-800 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Choose Aluminium if…</h4>
            </div>
            <ul className="space-y-2">
              {["You want the slimmest possible frames and maximum glass", "Your opening is wider than 3 metres", "You want a specific RAL colour or dual-colour finish", "You are making a long-term investment in your home", "You want the best thermal and acoustic performance"].map(p => (
                <li key={p} className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-amber-600 text-sm"></i></div>
                  <span className="text-zinc-600 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-600 rounded-lg"><i className="ri-price-tag-3-line text-white text-base"></i></div>
              <h4 className="font-bold text-zinc-800 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Choose uPVC if…</h4>
            </div>
            <ul className="space-y-2">
              {["Budget is the primary consideration", "Your opening is under 3 metres wide", "White or woodgrain finish suits your home", "You want a quick, cost-effective upgrade", "It is a rental property or short-term project"].map(p => (
                <li key={p} className="flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-arrow-right-s-line text-emerald-600 text-sm"></i></div>
                  <span className="text-zinc-600 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
